import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer,
} from "recharts";
import {
  Plus, RefreshCw, Download, TrendingUp, TrendingDown,
  CheckCircle2, Clock, AlertTriangle,
} from "lucide-react";
import { authFetch, API } from "../utils/api";
import { toast } from "sonner";
import { fmtAmt, CATEGORY_LABELS, ChartTooltip } from "./FinancePage.utils";
import { AddEntryModal } from "./FinancePage.AddEntryModal";
import AIUsageSection from "./FinancePage.AIUsage";
import ExpensePie from "./FinancePage.ExpensePie";

/* ── Main Page ────────────────────────────────────────────────────────────── */

export default function FinancePage() {
  const [summary, setSummary] = useState(null);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal,   setModal]   = useState(null);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [sumRes, entRes] = await Promise.all([
        authFetch(`${API}/admin/finance/summary`).then(r => r.json()),
        authFetch(`${API}/admin/finance/entries?limit=200`).then(r => r.json()),
      ]);
      setSummary(sumRes);
      setEntries(entRes.entries || []);
    } catch { toast.error("Error al cargar datos financieros"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  const handleDelete = async (id) => {
    if (!window.confirm("¿Eliminar este movimiento?")) return;
    try {
      const res = await authFetch(`${API}/admin/finance/entries/${id}`, { method: "DELETE" });
      const d = await res.json();
      if (d.ok) { toast.success("Eliminado"); loadAll(); }
      else toast.error(d.detail || "No se puede eliminar");
    } catch { toast.error("Error"); }
  };

  /* ── Computed metrics ── */
  const currencies   = summary?.by_currency || [];
  const primaryCur   = currencies[0] || { currency: "UYU", income: 0, expense: 0, margin: 0 };
  const totalIncome  = currencies.reduce((s, c) => s + (c.income  || 0), 0);
  const totalExpense = currencies.reduce((s, c) => s + (c.expense || 0), 0);
  const netMargin    = totalIncome - totalExpense;
  const marginPct    = totalIncome > 0 ? ((netMargin / totalIncome) * 100).toFixed(1) : 0;
  const isPositive   = netMargin >= 0;

  /* ── Monthly chart data (last 6 months from primary currency entries) ── */
  const monthlyChart = useMemo(() => {
    const now = new Date();
    const months = {};
    for (let i = 5; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
      const label = d.toLocaleDateString("es-UY", { month: "short" }).replace(".", "").toUpperCase();
      months[key] = { label, income: 0, expense: 0 };
    }
    entries.forEach(e => {
      if (!e.date) return;
      const d = new Date(e.date);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
      if (!months[key]) return;
      const amt = Number(e.amount) || 0;
      if (e.type === "income") months[key].income += amt;
      else months[key].expense += amt;
    });
    return Object.values(months);
  }, [entries]);

  /* ── Billing statuses (Iter92 — kept for the small panel; tabla retirada) ── */
  const billingStats = useMemo(() => {
    const paid    = entries.filter(e => e.type === "income" && e.source === "stripe");
    const manual  = entries.filter(e => e.type === "income" && e.source !== "stripe");
    const recent  = manual.filter(e => { const d = new Date(e.date); return (Date.now() - d) < 30 * 86400000; });
    const overdue = manual.filter(e => { const d = new Date(e.date); return (Date.now() - d) >= 30 * 86400000; });
    const sum = arr => arr.reduce((s, e) => s + (Number(e.amount) || 0), 0);
    return {
      paid:    { count: paid.length,    amount: sum(paid) },
      pending: { count: recent.length,  amount: sum(recent) },
      overdue: { count: overdue.length, amount: sum(overdue) },
    };
  }, [entries]);

  /* ── Export CSV (todo el historial) ── */
  const exportCSV = () => {
    const rows = [["Tipo", "Fecha", "Descripción", "Categoría", "Moneda", "Monto"]];
    entries.forEach(e => rows.push([
      e.type === "income" ? "Ingreso" : "Egreso",
      e.date, e.description,
      CATEGORY_LABELS[e.category] || e.category, e.currency, e.amount,
    ]));
    const csv = rows.map(r => r.join(",")).join("\n");
    const a = document.createElement("a");
    a.href = "data:text/csv;charset=utf-8," + encodeURIComponent(csv);
    a.download = `finanzas-${new Date().toISOString().slice(0, 10)}.csv`; a.click();
  };

  return (
    <div className="min-h-screen flex flex-col" style={{ background: "#0e0e0e", fontFamily: "'Inter', sans-serif" }}>

      {/* ── Sticky Header ─────────────────────────────────────────────────── */}
      <div
        className="flex-shrink-0 flex flex-col sm:flex-row sm:items-end justify-between gap-4 px-4 sm:px-8 pt-5 sm:pt-6 pb-4 sm:pb-5"
        style={{ background: "rgba(14,14,14,0.9)", backdropFilter: "blur(20px)", borderBottom: "1px solid rgba(255,255,255,0.05)" }}
      >
        <div className="min-w-0">
          <p className="text-[10px] font-black uppercase tracking-[0.2em] text-gray-500 mb-1">Resumen Financiero</p>
          <h1 className="text-3xl sm:text-4xl font-black tracking-tight text-white leading-none" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
            Capital <span style={{ color: "#ffd16c" }}>SYA</span>
          </h1>
        </div>
        <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
          <button
            onClick={loadAll}
            data-testid="finance-refresh-btn"
            className="p-2.5 rounded-2xl transition-all hover:scale-105 active:scale-95"
            style={{ background: "#1a1a1a", border: "1px solid rgba(255,255,255,0.08)" }}
          >
            <RefreshCw size={15} className={`text-gray-400 ${loading ? "animate-spin" : ""}`} />
          </button>
          <button
            onClick={exportCSV}
            data-testid="export-report-btn"
            className="hidden sm:flex items-center gap-2 px-4 py-2.5 rounded-2xl text-sm font-bold transition-colors"
            style={{ background: "#1a1a1a", border: "1px solid rgba(255,255,255,0.08)", color: "#9ca3af" }}
          >
            <Download size={14} /> Exportar Reporte
          </button>
          <button
            onClick={exportCSV}
            className="sm:hidden p-2.5 rounded-2xl"
            style={{ background: "#1a1a1a", border: "1px solid rgba(255,255,255,0.08)", color: "#9ca3af" }}
            title="Exportar CSV"
          >
            <Download size={15} />
          </button>
          <button
            onClick={() => setModal("income")}
            data-testid="new-record-btn"
            className="flex items-center gap-2 px-4 py-2.5 rounded-2xl text-sm font-black transition-all hover:brightness-110 active:scale-95"
            style={{ background: "#ffd16c", color: "#0e0e0e" }}
          >
            <Plus size={15} /> <span className="hidden sm:inline">Nuevo Registro</span><span className="sm:hidden">Nuevo</span>
          </button>
        </div>
      </div>

      {/* ── Main content ──────────────────────────────────────────────────── */}
      <div className="flex-1 overflow-y-auto px-4 sm:px-8 py-6 sm:py-8 space-y-6">

        {loading ? (
          <div className="flex items-center justify-center py-24">
            <RefreshCw size={24} className="animate-spin text-gray-600" />
          </div>
        ) : (
          <>
            {/* ── Metric cards bento grid ────────────────────────────────── */}
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">

              {/* Card 1 — Ganancia Neta (col-span-2) */}
              <div
                className="sm:col-span-2 md:col-span-2 relative overflow-hidden rounded-3xl p-6 sm:p-8 flex flex-col justify-between"
                style={{ background: "#1a1a1a", border: "1px solid rgba(255,255,255,0.07)", minHeight: 180 }}
              >
                {/* Background icon */}
                <div className="absolute right-5 bottom-3 text-[120px] leading-none pointer-events-none select-none"
                  style={{ color: "rgba(255,209,108,0.06)", fontWeight: 900 }}>
                  $
                </div>
                <div>
                  <p className="text-sm text-gray-400 mb-2 font-medium">Ganancia Neta Mensual</p>
                  <p className="text-4xl sm:text-5xl font-black tracking-tight text-white mb-4 break-words" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                    {fmtAmt(netMargin, primaryCur.currency)}
                  </p>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <span
                    className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-black"
                    style={isPositive
                      ? { background: "rgba(34,197,94,0.15)", color: "#4ade80", border: "1px solid rgba(34,197,94,0.2)" }
                      : { background: "rgba(239,68,68,0.15)", color: "#f87171", border: "1px solid rgba(239,68,68,0.2)" }}
                  >
                    {isPositive ? <TrendingUp size={11} /> : <TrendingDown size={11} />}
                    {isPositive ? "+" : ""}{marginPct}%
                  </span>
                  <span className="text-xs text-gray-500 italic">del total en ingresos</span>
                </div>
              </div>

              {/* Card 2 — Ingresos Totales */}
              <div
                className="rounded-3xl p-6 flex flex-col justify-between"
                style={{ background: "#1a1a1a", border: "1px solid rgba(255,255,255,0.07)" }}
              >
                <p className="text-sm text-gray-400 mb-3 font-medium">Ingresos Totales</p>
                <p className="text-3xl font-black text-white mb-4" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                  {fmtAmt(totalIncome, primaryCur.currency, true)}
                </p>
                {/* Goal progress bar */}
                <div>
                  <div className="h-1 bg-white/10 rounded-full overflow-hidden mb-1.5">
                    <div
                      className="h-full rounded-full transition-all duration-700"
                      style={{ width: `${Math.min(100, (totalIncome / (totalIncome * 1.25)) * 100)}%`, background: "#ffd16c" }}
                    />
                  </div>
                  <p className="text-[10px] font-bold tracking-widest uppercase text-gray-500">
                    META: {fmtAmt(totalIncome * 1.25, primaryCur.currency, true)}
                  </p>
                </div>
              </div>

              {/* Card 3 — Egresos Operativos */}
              <div
                className="rounded-3xl p-6 flex flex-col justify-between"
                style={{ background: "#1a1a1a", border: "1px solid rgba(255,255,255,0.07)" }}
              >
                <p className="text-sm text-gray-400 mb-3 font-medium">Egresos Operativos</p>
                <p className="text-3xl font-black mb-4" style={{ color: "#ff7351", fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                  {fmtAmt(totalExpense, primaryCur.currency, true)}
                </p>
                <div>
                  <div className="h-1 bg-white/10 rounded-full overflow-hidden mb-1.5">
                    <div
                      className="h-full rounded-full"
                      style={{ width: `${totalIncome > 0 ? Math.min(100, (totalExpense / totalIncome) * 100) : 0}%`, background: "#ff7351" }}
                    />
                  </div>
                  <p className="text-[10px] font-bold tracking-widest uppercase"
                    style={{ color: totalExpense / totalIncome < 0.5 ? "#4ade80" : "#f59e0b" }}>
                    {totalIncome > 0 ? `${((totalExpense / totalIncome) * 100).toFixed(0)}% de ingresos` : "SIN INGRESOS"}
                  </p>
                </div>
              </div>
            </div>

            {/* ── Chart + Billing Status ─────────────────────────────────── */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">

              {/* Chart — Flujo de Caja Anual */}
              <div
                className="lg:col-span-2 rounded-3xl p-8"
                style={{ background: "#1a1a1a", border: "1px solid rgba(255,255,255,0.07)" }}
              >
                <div className="flex items-center justify-between mb-6">
                  <p className="text-lg font-black text-white" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                    Flujo de Caja Anual
                  </p>
                  <div className="flex items-center gap-4 text-xs text-gray-400">
                    <span className="flex items-center gap-1.5">
                      <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: "#ffd16c" }} />
                      Ingresos
                    </span>
                    <span className="flex items-center gap-1.5">
                      <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: "#5c2424" }} />
                      Egresos
                    </span>
                  </div>
                </div>
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={monthlyChart} barGap={4} barSize={28}>
                    <CartesianGrid vertical={false} stroke="rgba(255,255,255,0.04)" />
                    <XAxis dataKey="label" tick={{ fill: "#6b7280", fontSize: 11, fontWeight: 600 }} axisLine={false} tickLine={false} />
                    <YAxis hide />
                    <Tooltip content={<ChartTooltip />} cursor={{ fill: "rgba(255,255,255,0.04)", radius: 8 }} />
                    <Bar dataKey="income" name="Ingresos" fill="#ffd16c" radius={[6, 6, 0, 0]} />
                    <Bar dataKey="expense" name="Egresos" fill="#5c2424" radius={[6, 6, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>

              {/* Billing Status Card */}
              <div
                className="rounded-3xl p-8 flex flex-col"
                style={{ background: "#1a1a1a", border: "1px solid rgba(255,255,255,0.07)" }}
              >
                <p className="text-lg font-black text-white mb-6" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                  Estados de Facturación
                </p>
                <div className="space-y-0 flex-1 divide-y" style={{ borderColor: "rgba(255,255,255,0.05)" }}>
                  {/* Pagadas */}
                  <div className="flex items-center justify-between py-4">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: "rgba(34,197,94,0.15)" }}>
                        <CheckCircle2 size={16} className="text-green-400" />
                      </div>
                      <div>
                        <p className="text-sm font-bold text-white">Pagadas</p>
                        <p className="text-[11px] text-gray-500">{billingStats.paid.count} via Stripe</p>
                      </div>
                    </div>
                    <p className="text-sm font-black text-green-400">{fmtAmt(billingStats.paid.amount, primaryCur.currency, true)}</p>
                  </div>
                  {/* Pendientes */}
                  <div className="flex items-center justify-between py-4">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: "rgba(255,209,108,0.15)" }}>
                        <Clock size={16} style={{ color: "#ffd16c" }} />
                      </div>
                      <div>
                        <p className="text-sm font-bold text-white">Pendientes</p>
                        <p className="text-[11px] text-gray-500">{billingStats.pending.count} ingresos manuales</p>
                      </div>
                    </div>
                    <p className="text-sm font-black" style={{ color: "#ffd16c" }}>{fmtAmt(billingStats.pending.amount, primaryCur.currency, true)}</p>
                  </div>
                  {/* Vencidas */}
                  <div className="flex items-center justify-between py-4">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: "rgba(255,115,81,0.15)" }}>
                        <AlertTriangle size={16} style={{ color: "#ff7351" }} />
                      </div>
                      <div>
                        <p className="text-sm font-bold text-white">Vencidas</p>
                        <p className="text-[11px] text-gray-500">{billingStats.overdue.count} {billingStats.overdue.count === 1 ? "crítica" : "críticas"}</p>
                      </div>
                    </div>
                    <p className="text-sm font-black" style={{ color: "#ff7351" }}>{fmtAmt(billingStats.overdue.amount, primaryCur.currency, true)}</p>
                  </div>
                </div>
                <button
                  onClick={() => setModal("income")}
                  className="mt-4 text-center text-sm font-bold transition-colors hover:underline"
                  style={{ color: "#ffd16c" }}
                >
                  + Nuevo Registro
                </button>
              </div>
            </div>

            {/* ── Actividad (3/4) + 2 Pies apilados (1/4) ──────────────── */}
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-4" data-testid="finance-activity-row">
              <AIUsageSection
                entries={entries}
                className="lg:col-span-3"
                onAddEntry={() => setModal("income")}
                onDeleteEntry={handleDelete}
              />
              <div className="grid grid-rows-2 gap-4">
                <ExpensePie entries={entries} currency={primaryCur.currency} mode="expense" />
                <ExpensePie entries={entries} currency={primaryCur.currency} mode="income" />
              </div>
            </div>
          </>
        )}
      </div>

      {modal && <AddEntryModal type={modal} onClose={() => setModal(null)} onSaved={loadAll} />}
    </div>
  );
}
